import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axiosInstances";
import touristicPlacesQueryKeys, {
  acExpoTouristicPlacesQueryKeys,
} from "../query-keys/touristic-places.query-keys";
import {
  FiltersExtended,
  FilterEventType,
  Paginated,
  PaginatedResponse,
  QueryArgs,
  TouristicPlaceDetail,
  Sortable,
  TouristicPlaceItemType,
  TouristicPlaceSummary,
  TouristicPlaceType,
} from "@ac-expo/api-types";

const getUserTouristicPlaces = async () => {
  const { data } = await privateAxios.get<TouristicPlaceItemType[]>(
    "/touristic-places/mine"
  );

  return data;
};

export const useUserTouristicPlacesQuery = () => {
  return useQuery({
    queryKey: touristicPlacesQueryKeys.me(),
    queryFn: () => getUserTouristicPlaces(),
  });
};

const getTouristicPlacesForAcExpo = async (
  params: Sortable<Paginated<Omit<FilterEventType, "sortBy">>>
) => {
  const { data } = await privateAxios.get<
    PaginatedResponse<TouristicPlaceItemType>
  >("/touristic-places/all", { params });
  return data;
};

export const useGetAllTouristicPlacesQuery = (
  params: Sortable<Paginated<Omit<FilterEventType, "sortBy">>>,
  options: QueryArgs<PaginatedResponse<TouristicPlaceItemType>>
) => {
  return useQuery({
    queryKey: acExpoTouristicPlacesQueryKeys.list(params),
    queryFn: () => getTouristicPlacesForAcExpo(params),
    ...options,
  });
};

const getTouristicPlace = async (id: string) => {
  const { data } = await privateAxios.get<TouristicPlaceType>(
    `/touristic-places/${id}`
  );
  return data;
};

export const useTouristicPlaceQuery = (
  id: string,
  options?: QueryArgs<TouristicPlaceType>
) => {
  return useQuery({
    queryKey: touristicPlacesQueryKeys.get(id),
    queryFn: () => getTouristicPlace(id),
    ...options,
  });
};

const getTouristicPlaces = async (params: string) => {
  const res = await privateAxios.get<TouristicPlaceSummary[]>(
    `/touristic-places/public?${params}`
  );
  return res.data;
};

export const useGetTouristicPlaces = (
  filters: FiltersExtended,
  isEnabled = false
) => {
  const parsedFilters = {
    sortBy: filters.sortBy,
    ...(filters.dates && {
      dates: filters.dates,
    }),
    ...(filters.dates === "custom" &&
      filters.customDates && {
        startDate: filters.customDates[0],
        endDate: filters.customDates[1],
      }),
    places: filters.places,
    eventTypes: filters.eventTypes,
    placeTypes: filters.placeTypes,
    entryFree: filters.entryFree,
    entryPaid: filters.entryPaid,
    venueIndoor: filters.venueIndoor,
    venueOutdoor: filters.venueOutdoor,
    ...(filters.searchText && { searchText: filters.searchText }),
  };

  const filteredParsedFilters = Object.fromEntries(
    Object.entries(parsedFilters).filter(([_, value]) => value !== undefined)
  );
  const params = new URLSearchParams(
    filteredParsedFilters as Record<string, string>
  ).toString();

  return useQuery({
    queryKey: touristicPlacesQueryKeys.list(params),
    queryFn: () => getTouristicPlaces(params),
    enabled: isEnabled,
  });
};

const getPlaceTouristicDetail = async (id: string) => {
  const res = await privateAxios.get<TouristicPlaceDetail>(
    `/touristic-places/public/${id}`
  );
  return res.data;
};

export const useGetTouristicPlaceDetail = (id: string) => {
  return useQuery({
    queryKey: touristicPlacesQueryKeys.detail(id),
    queryFn: () => getPlaceTouristicDetail(id),
  });
};
